import { OperationsBoard } from './OperationsBoard.js';
import { OperationAgenda, type OperationSchedulePreset } from './OperationAgenda.js';
import {
  MobileOperationalNavigation,
  OperationalNavigation,
  focusOperationalSection,
} from './OperationalNavigation.js';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { AuthScreen, hasInvitationAction } from './AuthScreen.js';
import { OperationCreator, OperationDetailView } from './OperationWorkflow.js';
import { OrganizationSetup } from './OrganizationSetup.js';
import { InitialResources } from './InitialResources.js';
import { PilotReadiness } from './PilotReadiness.js';
import { PlatformAdministration } from './PlatformAdministration.js';
import { OrganizationProfile } from './OrganizationProfile.js';
import { OrganizationTeam } from './OrganizationTeam.js';
import { ShiftHandoffPanel } from './ShiftHandoffPanel.js';
import { authClient } from './auth-client.js';
import webPackage from '../package.json';
import {
  defaultOperationalSurface,
  hasTenantCapability,
  hasTenantFixedRole,
  invitationRoleOptions,
  tenantCapability,
  tenantRoleLabel,
  type InvitableTenantRole,
  type TenantAccess,
} from './tenant-access.js';

type IconName =
  'aircraft' | 'calendar' | 'dashboard' | 'operations' | 'people' | 'pending' | 'shield';

type WorkspaceView = 'platform' | 'operations' | 'tenant-administration';

interface NavigationItem {
  readonly label: string;
  readonly icon: IconName;
  readonly view: WorkspaceView;
}

const iconPaths: Readonly<Record<IconName, string>> = {
  aircraft: 'M3 12h18M12 3l3 9-3 9-3-9 3-9Zm-7 9 2 5m12-5-2 5',
  calendar: 'M6 3v3m12-3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v14H4V6a1 1 0 0 1 1-1Z',
  dashboard: 'M4 4h6v7H4V4Zm10 0h6v4h-6V4ZM4 15h6v5H4v-5Zm10-3h6v8h-6v-8Z',
  operations: 'm3 13 18-8-7 15-3-6-6-1-2-2 7-2 2-6 2 1-1 7 5 3Z',
  people:
    'M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m7-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm13 10v-2a4 4 0 0 0-3-3.87m-2-11.96a4 4 0 0 1 0 7.75',
  pending: 'M12 8v5l3 2m6-3a9 9 0 1 1-9-9 9 9 0 0 1 9 9Z',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Zm-3-10 2 2 4-5',
};

interface OrganizationContext {
  readonly organizationId: string;
  readonly membershipId: string;
  readonly legalName: string;
  readonly tradeName: string;
}

interface OrganizationsResponse {
  readonly user: { readonly id: string; readonly name: string; readonly email: string };
  readonly organizations: readonly OrganizationContext[];
  readonly capabilities?: { readonly platformAdministration?: boolean };
}

interface OrganizationSummary {
  readonly operationsOpen: number;
  readonly aircraftActive: number;
}

interface OrganizationAdminMetrics extends OrganizationSummary {
  readonly operationsTotal: number;
  readonly basesActive: number;
  readonly membersActive: number;
  readonly invitationsPending: number;
}

interface OrganizationDashboard {
  readonly organization: OrganizationContext;
  readonly summary: OrganizationSummary;
  readonly operations: readonly {
    readonly id: string;
    readonly referenceCode: string;
    readonly state: string;
    readonly registrationMark: string | null;
    readonly plannedDeparture: string | null;
    readonly origin: string | null;
    readonly destination: string | null;
  }[];
}

interface OrganizationAdminSummary {
  readonly organization: OrganizationContext;
  readonly summary: OrganizationAdminMetrics;
  readonly homologation?: { readonly tenantIsolationAvailable: boolean };
}

interface TenantIsolationProbeObservation {
  readonly status: number;
  readonly code: string | null;
}

interface TenantIsolationProbeResult {
  readonly passed: boolean;
  readonly ownTenant: TenantIsolationProbeObservation;
  readonly foreignTenant: TenantIsolationProbeObservation;
}

type OrganizationCapabilitiesResponse = TenantAccess;

interface InvitationCreatedResponse {
  readonly invitation: {
    readonly id: string;
    readonly email: string;
    readonly expiresAt: string;
    readonly role: {
      readonly key: InvitableTenantRole;
      readonly name: string;
    };
  };
  readonly delivery: 'scheduled';
}

class RequestError extends Error {
  constructor(readonly status: number) {
    super(`REQUEST_FAILED:${status}`);
  }
}

async function getJson<ResponseBody>(url: string): Promise<ResponseBody> {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) throw new RequestError(response.status);
  return (await response.json()) as ResponseBody;
}

function ProductIcon({ name }: { readonly name: IconName }) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <path
        d={iconPaths[name]}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Brand({ compact = false }: { readonly compact?: boolean }) {
  return (
    <a className={`brand${compact ? ' brandCompact' : ''}`} href="#main-content">
      <span className="brandMark" aria-hidden="true">
        A
      </span>
      <span>
        <strong>Aeronexo</strong>
        <small>Coordenação de Voo</small>
      </span>
    </a>
  );
}

function LoadingScreen() {
  return (
    <main className="fullPageStatus" aria-live="polite">
      <span className="brandMark" aria-hidden="true">
        A
      </span>
      <div className="loadingPulse" aria-hidden="true" />
      <strong>Preparando seu ambiente</strong>
      <span>Validando sua sessão e os acessos autorizados…</span>
    </main>
  );
}

interface OrganizationChooserProps {
  readonly canAccessPlatform: boolean;
  readonly isSigningOut: boolean;
  readonly organizations: readonly OrganizationContext[];
  readonly signOutError: string | undefined;
  readonly userName: string;
  readonly onSelectOrganization: (organization: OrganizationContext) => void;
  readonly onSelectPlatform: () => void;
  readonly onSignOut: () => void;
}

function OrganizationChooser({
  canAccessPlatform,
  isSigningOut,
  organizations,
  signOutError,
  userName,
  onSelectOrganization,
  onSelectPlatform,
  onSignOut,
}: OrganizationChooserProps) {
  return (
    <main className="organizationChooser" id="main-content">
      <Brand />
      <section>
        <span>Conta pessoal de {userName}</span>
        <h1>Onde você deseja trabalhar?</h1>
        <p>Selecione a organização para acessar seus voos e sua equipe.</p>
        <div className="organizationOptions">
          {canAccessPlatform ? (
            <button type="button" onClick={onSelectPlatform}>
              <span className="organizationInitial platformInitial" aria-hidden="true">
                <ProductIcon name="shield" />
              </span>
              <span>
                <strong>Administração da plataforma</strong>
                <small>Gerenciar a plataforma</small>
              </span>
              <span aria-hidden="true">→</span>
            </button>
          ) : null}
          {organizations.map((organization) => (
            <button
              key={organization.organizationId}
              type="button"
              onClick={() => onSelectOrganization(organization)}
            >
              <span className="organizationInitial" aria-hidden="true">
                {organization.tradeName.charAt(0).toUpperCase()}
              </span>
              <span>
                <strong>{organization.tradeName}</strong>
                <small>Acessar operações e equipe</small>
              </span>
              <span aria-hidden="true">→</span>
            </button>
          ))}
        </div>
        <button
          className="chooserSignOut"
          disabled={isSigningOut}
          type="button"
          onClick={onSignOut}
        >
          {isSigningOut ? 'Encerrando…' : 'Sair desta conta'}
        </button>
        {signOutError ? (
          <div className="inlineError" role="alert">
            {signOutError}
          </div>
        ) : null}
      </section>
    </main>
  );
}

interface NoOrganizationAccessProps {
  readonly email: string;
  readonly isRefreshing: boolean;
  readonly isSigningOut: boolean;
  readonly signOutError: string | undefined;
  readonly onRefresh: () => void;
  readonly onSignOut: () => void;
}

function NoOrganizationAccess({
  email,
  isRefreshing,
  isSigningOut,
  signOutError,
  onRefresh,
  onSignOut,
}: NoOrganizationAccessProps) {
  return (
    <main className="fullPageStatus accessPending" id="main-content">
      <span className="statusIllustration accessIllustration" aria-hidden="true">
        <ProductIcon name="people" />
      </span>
      <strong>Nenhuma organização liberada</strong>
      <span>
        Sua conta <strong>{email}</strong> está ativa, mas ainda não possui vínculo operacional.
        Aceite o convite recebido por e-mail ou peça ao administrador da sua empresa que envie um
        convite para este endereço.
      </span>
      <div className="statusActions">
        <button className="primaryButton" disabled={isRefreshing} type="button" onClick={onRefresh}>
          {isRefreshing ? 'Atualizando…' : 'Atualizar acesso'}
        </button>
        <button disabled={isSigningOut} type="button" onClick={onSignOut}>
          {isSigningOut ? 'Encerrando…' : 'Sair da conta'}
        </button>
      </div>
      {signOutError ? (
        <div className="inlineError" role="alert">
          {signOutError}
        </div>
      ) : null}
    </main>
  );
}

function SystemVersion() {
  const [version, setVersion] = useState(webPackage.version);
  const [release, setRelease] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    void fetch('/v1/version', { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) return;
        const value = (await response.json()) as { version?: string; release?: string };
        if (value.version) setVersion(value.version);
        if (value.release) setRelease(value.release);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  return (
    <small className="systemVersion" title={release ? `Versão ${version} · ${release}` : undefined}>
      v{version}
    </small>
  );
}

export function App() {
  const { data: session, error, isPending } = authClient.useSession();
  const authenticationParameters = new URLSearchParams(window.location.search);
  const hasAuthenticationAction = authenticationParameters.has('token') || hasInvitationAction;

  let content;
  if (isPending) content = <LoadingScreen />;
  else if (hasAuthenticationAction) {
    content = (
      <AuthScreen
        authenticated={Boolean(session)}
        authenticatedEmail={session?.user.email}
        serviceUnavailable={Boolean(error)}
      />
    );
  } else if (!session) {
    content = <AuthScreen serviceUnavailable={Boolean(error)} />;
  } else {
    content = <OperationalWorkspace userEmail={session.user.email} userName={session.user.name} />;
  }

  return (
    <>
      {content}
      <SystemVersion />
    </>
  );
}

interface OperationalWorkspaceProps {
  readonly userName: string;
  readonly userEmail: string;
}

function OperationalWorkspace({ userEmail, userName }: OperationalWorkspaceProps) {
  const [organizations, setOrganizations] = useState<readonly OrganizationContext[]>([]);
  const [canAccessPlatform, setCanAccessPlatform] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>();
  const [activeView, setActiveView] = useState<WorkspaceView>('operations');
  const [selectedOperationId, setSelectedOperationId] = useState<string>();
  const [operationTask, setOperationTask] = useState<{ section: string; sequence: number }>();
  const [dashboard, setDashboard] = useState<OrganizationDashboard>();
  const [administratorSummary, setAdministratorSummary] = useState<OrganizationAdminSummary>();
  const [tenantAccess, setTenantAccess] = useState<TenantAccess>();
  const [hasLoadedAccess, setHasLoadedAccess] = useState(false);
  const [loadingError, setLoadingError] = useState(false);
  const [contextError, setContextError] = useState<string>();
  const [isLoadingContext, setIsLoadingContext] = useState(true);
  const [signOutError, setSignOutError] = useState<string>();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const contextRequestId = useRef(0);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    setSignOutError(undefined);

    const result = await authClient.signOut();
    if (result.error) {
      setSignOutError('Não foi possível encerrar a sessão. Tente novamente.');
      setIsSigningOut(false);
      return;
    }

    window.location.replace('/');
  };

  const handleExpiredSession = useCallback(async () => {
    await authClient.signOut();
    window.location.replace('/');
  }, []);

  const selectPlatformWorkspace = useCallback(() => {
    contextRequestId.current += 1;
    setSelectedWorkspace('platform');
    setActiveView('platform');
    setDashboard(undefined);
    setAdministratorSummary(undefined);
    setTenantAccess(undefined);
    setContextError(undefined);
    setIsLoadingContext(false);
  }, []);

  const loadOrganization = useCallback(
    async (organization: OrganizationContext) => {
      const requestId = contextRequestId.current + 1;
      contextRequestId.current = requestId;
      setSelectedWorkspace(organization.organizationId);
      setSelectedOperationId(undefined);
      setOperationTask(undefined);
      setActiveView('operations');
      setDashboard(undefined);
      setAdministratorSummary(undefined);
      setTenantAccess(undefined);
      setIsLoadingContext(true);
      setContextError(undefined);

      let access: OrganizationCapabilitiesResponse;
      try {
        access = await getJson<OrganizationCapabilitiesResponse>(
          `/v1/organizations/${organization.organizationId}/capabilities`,
        );
      } catch (error) {
        if (contextRequestId.current !== requestId) return;
        if (error instanceof RequestError && error.status === 401) {
          await handleExpiredSession();
          return;
        }
        setContextError('Não foi possível confirmar as permissões desta organização.');
        setIsLoadingContext(false);
        return;
      }
      if (contextRequestId.current !== requestId) return;
      setTenantAccess(access);
      if (access.registrationRequired) {
        setIsLoadingContext(false);
        return;
      }

      if (!hasTenantFixedRole(access)) {
        setContextError('Aguardando atribuição de uma função fixa nesta organização.');
        setIsLoadingContext(false);
        return;
      }

      if (!hasTenantCapability(access, tenantCapability.operationView)) {
        setContextError('Sua função não permite visualizar as operações desta organização.');
        setIsLoadingContext(false);
        return;
      }

      const dashboardPromise = getJson<OrganizationDashboard>(
        `/v1/organizations/${organization.organizationId}/dashboard`,
      );
      const administratorPromise = hasTenantCapability(
        access,
        tenantCapability.organizationAdminister,
      )
        ? getJson<OrganizationAdminSummary>(
            `/v1/organizations/${organization.organizationId}/admin/summary`,
          )
        : Promise.resolve(undefined);

      const [dashboardResult, administratorResult] = await Promise.allSettled([
        dashboardPromise,
        administratorPromise,
      ]);
      if (contextRequestId.current !== requestId) return;

      if (dashboardResult.status === 'rejected') {
        if (
          dashboardResult.reason instanceof RequestError &&
          dashboardResult.reason.status === 401
        ) {
          await handleExpiredSession();
          return;
        }
        setContextError('Não foi possível abrir esta organização. Tente novamente.');
        setIsLoadingContext(false);
        return;
      }

      setDashboard(dashboardResult.value);
      if (administratorResult.status === 'fulfilled') {
        setAdministratorSummary(administratorResult.value);
      } else if (
        administratorResult.reason instanceof RequestError &&
        administratorResult.reason.status === 401
      ) {
        await handleExpiredSession();
        return;
      } else if (hasTenantCapability(access, tenantCapability.organizationAdminister)) {
        setContextError(
          'A operação foi carregada, mas a administração da organização está temporariamente indisponível.',
        );
      }
      setIsLoadingContext(false);
    },
    [handleExpiredSession],
  );

  useEffect(() => {
    let active = true;

    const load = async () => {
      setIsLoadingContext(true);
      setLoadingError(false);
      setContextError(undefined);
      setHasLoadedAccess(false);
      setSelectedWorkspace(undefined);
      setDashboard(undefined);
      setAdministratorSummary(undefined);
      setTenantAccess(undefined);
      contextRequestId.current += 1;

      try {
        const response = await getJson<OrganizationsResponse>('/v1/organizations');
        if (!active) return;

        const platformAdministration = response.capabilities?.platformAdministration === true;
        setOrganizations(response.organizations);
        setCanAccessPlatform(platformAdministration);
        setHasLoadedAccess(true);

        const workspaceCount = response.organizations.length + (platformAdministration ? 1 : 0);
        if (workspaceCount === 1 && platformAdministration) {
          selectPlatformWorkspace();
          return;
        }
        if (workspaceCount === 1) {
          const organization = response.organizations[0];
          if (organization) await loadOrganization(organization);
          return;
        }
        setIsLoadingContext(false);
      } catch (error) {
        if (!active) return;
        if (error instanceof RequestError && error.status === 401) {
          await handleExpiredSession();
          return;
        }
        setHasLoadedAccess(true);
        setLoadingError(true);
        setIsLoadingContext(false);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [handleExpiredSession, loadOrganization, reloadKey, selectPlatformWorkspace]);

  const workspaceCount = organizations.length + (canAccessPlatform ? 1 : 0);
  const requestedOrganization = organizations.find(
    (organization) => organization.organizationId === selectedWorkspace,
  );

  if (!hasLoadedAccess && isLoadingContext) return <LoadingScreen />;

  if (loadingError) {
    return (
      <main className="fullPageStatus fullPageError" id="main-content">
        <span className="statusIllustration" aria-hidden="true">
          !
        </span>
        <strong>Não foi possível confirmar seus acessos</strong>
        <span>Verifique sua conexão e tente novamente. Sua sessão continua protegida.</span>
        <div className="statusActions">
          <button
            className="primaryButton"
            type="button"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Tentar novamente
          </button>
          <button disabled={isSigningOut} type="button" onClick={() => void handleSignOut()}>
            {isSigningOut ? 'Encerrando…' : 'Sair da conta'}
          </button>
        </div>
        {signOutError ? (
          <div className="inlineError" role="alert">
            {signOutError}
          </div>
        ) : null}
      </main>
    );
  }

  if (workspaceCount === 0) {
    return (
      <NoOrganizationAccess
        email={userEmail}
        isRefreshing={isLoadingContext}
        isSigningOut={isSigningOut}
        signOutError={signOutError}
        onRefresh={() => setReloadKey((key) => key + 1)}
        onSignOut={() => void handleSignOut()}
      />
    );
  }

  if (!selectedWorkspace && workspaceCount > 1) {
    return (
      <OrganizationChooser
        canAccessPlatform={canAccessPlatform}
        isSigningOut={isSigningOut}
        organizations={organizations}
        signOutError={signOutError}
        userName={userName}
        onSelectOrganization={(organization) => void loadOrganization(organization)}
        onSelectPlatform={selectPlatformWorkspace}
        onSignOut={() => void handleSignOut()}
      />
    );
  }

  if (requestedOrganization && tenantAccess?.registrationRequired) {
    return (
      <main className="registrationPage" id="main-content">
        <header className="registrationHeader">
          <strong>Aeronexo</strong>
          <button type="button" disabled={isSigningOut} onClick={() => void handleSignOut()}>
            Sair da conta
          </button>
        </header>
        {signOutError ? <p role="alert">{signOutError}</p> : null}
        {hasTenantCapability(tenantAccess, tenantCapability.organizationAdminister) ? (
          tenantAccess.registrationProgress?.profileComplete ? (
            <InitialResources
              key={requestedOrganization.organizationId}
              organizationId={requestedOrganization.organizationId}
              userEmail={userEmail}
              baseComplete={tenantAccess.registrationProgress.baseComplete}
              onExpiredSession={handleExpiredSession}
              onChanged={() => loadOrganization(requestedOrganization)}
            />
          ) : (
            <OrganizationProfile
              key={requestedOrganization.organizationId}
              organizationId={requestedOrganization.organizationId}
              onboarding
              userName={userName}
              userEmail={userEmail}
              onExpiredSession={handleExpiredSession}
              onChanged={() => loadOrganization(requestedOrganization)}
            />
          )
        ) : (
          <section className="organizationProfilePanel">
            <h1>Cadastro da empresa em andamento</h1>
            <p>
              O administrador da organização precisa concluir o cadastro para liberar o acesso da
              equipe.
            </p>
            <button type="button" onClick={() => void loadOrganization(requestedOrganization)}>
              Verificar novamente
            </button>
          </section>
        )}
      </main>
    );
  }

  if (selectedWorkspace !== 'platform' && !dashboard) {
    if (isLoadingContext) return <LoadingScreen />;
    if (requestedOrganization && tenantAccess && !hasTenantFixedRole(tenantAccess)) {
      return (
        <main className="fullPageStatus accessPending" id="main-content">
          <span className="statusIllustration accessIllustration" aria-hidden="true">
            <ProductIcon name="shield" />
          </span>
          <strong>Aguardando atribuição de função</strong>
          <span>
            Seu vínculo com <strong>{requestedOrganization.tradeName}</strong> está ativo, mas ainda
            não há uma função efetiva para visualizar ou alterar dados. Peça a um administrador da
            organização que atribua Administrador, Coordenador ou Visualizador.
          </span>
          <div className="statusActions">
            <button
              className="primaryButton"
              type="button"
              onClick={() => void loadOrganization(requestedOrganization)}
            >
              Atualizar acesso
            </button>
            {workspaceCount > 1 ? (
              <button
                type="button"
                onClick={() => {
                  setSelectedWorkspace(undefined);
                  setTenantAccess(undefined);
                  setContextError(undefined);
                }}
              >
                Escolher ambiente
              </button>
            ) : null}
            <button disabled={isSigningOut} type="button" onClick={() => void handleSignOut()}>
              {isSigningOut ? 'Encerrando…' : 'Sair da conta'}
            </button>
          </div>
        </main>
      );
    }
    return (
      <main className="fullPageStatus fullPageError" id="main-content">
        <span className="statusIllustration" aria-hidden="true">
          !
        </span>
        <strong>Não foi possível abrir esta organização</strong>
        <span>{contextError ?? 'O acesso não foi confirmado. Escolha outro ambiente.'}</span>
        <div className="statusActions">
          {requestedOrganization ? (
            <button
              className="primaryButton"
              type="button"
              onClick={() => void loadOrganization(requestedOrganization)}
            >
              Tentar novamente
            </button>
          ) : null}
          {workspaceCount > 1 ? (
            <button
              type="button"
              onClick={() => {
                setSelectedWorkspace(undefined);
                setTenantAccess(undefined);
                setContextError(undefined);
              }}
            >
              Escolher ambiente
            </button>
          ) : null}
          <button disabled={isSigningOut} type="button" onClick={() => void handleSignOut()}>
            {isSigningOut ? 'Encerrando…' : 'Sair da conta'}
          </button>
        </div>
        {signOutError ? (
          <div className="inlineError" role="alert">
            {signOutError}
          </div>
        ) : null}
      </main>
    );
  }

  const isPlatformWorkspace = selectedWorkspace === 'platform';
  const organization = dashboard?.organization;
  const canAdministerOrganization = hasTenantCapability(
    tenantAccess,
    tenantCapability.organizationAdminister,
  );
  const navigationItems: readonly NavigationItem[] = isPlatformWorkspace
    ? [{ label: 'Visão geral', icon: 'dashboard', view: 'platform' }]
    : [
        { label: 'Agenda de voos', icon: 'calendar', view: 'operations' },
        ...(canAdministerOrganization
          ? ([
              {
                label: 'Administração',
                icon: 'shield',
                view: 'tenant-administration',
              },
            ] as const)
          : []),
      ];
  const navigateOperation = (section: string) => {
    setActiveView('operations');
    setOperationTask((current) => ({ section, sequence: (current?.sequence ?? 0) + 1 }));
  };
  const operationalNavigation = !isPlatformWorkspace ? (
    <OperationalNavigation
      canCreate={hasTenantCapability(tenantAccess, tenantCapability.operationCreate)}
      canViewAgenda={hasTenantCapability(tenantAccess, tenantCapability.agendaView)}
      hasOperation={Boolean(selectedOperationId)}
      onNavigate={navigateOperation}
    />
  ) : null;
  const canCreateOperation = hasTenantCapability(tenantAccess, tenantCapability.operationCreate);
  const canViewAgenda = hasTenantCapability(tenantAccess, tenantCapability.agendaView);

  const initials = userName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  const today = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  }).format(new Date());
  const activeWorkspaceLabel = isPlatformWorkspace
    ? 'Administração da plataforma'
    : (organization?.tradeName ?? 'Organização');

  const changeWorkspace = (workspaceId: string) => {
    if (workspaceId === 'platform') {
      selectPlatformWorkspace();
      return;
    }
    const selected = organizations.find((candidate) => candidate.organizationId === workspaceId);
    if (selected) void loadOrganization(selected);
  };

  return (
    <div className="appShell">
      <a className="skipLink" href="#main-content">
        Ir para o conteúdo principal
      </a>

      <aside className="desktopSidebar">
        <Brand />

        <div className="organizationContext">
          <span>Organização</span>
          {workspaceCount > 1 ? (
            <select
              aria-label="Alterar ambiente ativo"
              disabled={isLoadingContext}
              value={selectedWorkspace}
              onChange={(event) => changeWorkspace(event.target.value)}
            >
              {canAccessPlatform ? (
                <option value="platform">Administração da plataforma</option>
              ) : null}
              {organizations.map((candidate) => (
                <option key={candidate.organizationId} value={candidate.organizationId}>
                  {candidate.tradeName}
                </option>
              ))}
            </select>
          ) : (
            <strong>{activeWorkspaceLabel}</strong>
          )}
          <small>
            {isPlatformWorkspace ? 'Administração da plataforma' : tenantRoleLabel(tenantAccess)}
          </small>
        </div>

        <nav className="desktopNavigation" aria-label="Navegação principal">
          <div className="navigationGroup">
            <span className="navigationLabel">
              {isPlatformWorkspace ? 'Plataforma' : 'Coordenação de Voo'}
            </span>
            {navigationItems.map((item) => (
              <button
                aria-current={activeView === item.view ? 'page' : undefined}
                className={activeView === item.view ? 'active' : undefined}
                key={item.view}
                type="button"
                onClick={() => {
                  if (item.view === 'operations')
                    navigateOperation(
                      hasTenantCapability(tenantAccess, tenantCapability.agendaView)
                        ? 'operation-agenda'
                        : 'operations-list',
                    );
                  else setActiveView(item.view);
                }}
              >
                <ProductIcon name={item.icon} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          {operationalNavigation}
        </nav>

        <div className="sidebarAccount">
          <span className="userAvatar" aria-hidden="true">
            {initials}
          </span>
          <span className="accountIdentity">
            <strong>{userName}</strong>
            <small>{userEmail}</small>
          </span>
          <button
            aria-label={`Sair da conta de ${userName}`}
            disabled={isSigningOut}
            title="Sair"
            type="button"
            onClick={() => void handleSignOut()}
          >
            ↗
          </button>
        </div>
      </aside>

      <div className="appWorkspace">
        <header className="mobileHeader">
          <Brand compact />
          {!isPlatformWorkspace ? (
            <details
              className="mobileFunctionMenu"
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.currentTarget.open = false;
                  event.currentTarget.querySelector('summary')?.focus();
                }
              }}
            >
              <summary>Funções</summary>
              <nav aria-label="Funções da coordenação">
                {navigationItems.map((item) => (
                  <button
                    aria-current={activeView === item.view ? 'page' : undefined}
                    className="mobileMenuDestination"
                    key={item.view}
                    type="button"
                    onClick={(event) => {
                      event.currentTarget.closest('details')?.removeAttribute('open');
                      if (item.view === 'operations') {
                        navigateOperation(canViewAgenda ? 'operation-agenda' : 'operations-list');
                      } else {
                        setActiveView(item.view);
                      }
                    }}
                  >
                    <ProductIcon name={item.icon} />
                    <span>{item.label}</span>
                  </button>
                ))}
                {operationalNavigation}
              </nav>
            </details>
          ) : null}
          <div>
            {workspaceCount > 1 ? (
              <select
                aria-label="Alterar ambiente ativo"
                disabled={isLoadingContext}
                value={selectedWorkspace}
                onChange={(event) => changeWorkspace(event.target.value)}
              >
                {canAccessPlatform ? (
                  <option value="platform">Administração da plataforma</option>
                ) : null}
                {organizations.map((candidate) => (
                  <option key={candidate.organizationId} value={candidate.organizationId}>
                    {candidate.tradeName}
                  </option>
                ))}
              </select>
            ) : (
              <span>{activeWorkspaceLabel}</span>
            )}
            <button disabled={isSigningOut} type="button" onClick={() => void handleSignOut()}>
              {isSigningOut ? 'Saindo…' : 'Sair'}
            </button>
          </div>
        </header>

        <main className="appMain" id="main-content">
          <PageHeading
            activeView={activeView}

            organization={organization}
            tenantAccess={tenantAccess}
            today={today}
          />

          {signOutError ? (
            <div className="inlineError" role="alert">
              {signOutError}
            </div>
          ) : null}
          {contextError && dashboard ? (
            <div className="inlineError" role="alert">
              {contextError}
            </div>
          ) : null}
          {isLoadingContext ? (
            <div aria-label="Carregando ambiente" className="contextProgress" role="progressbar" />
          ) : null}

          {activeView === 'platform' ? (
            <PlatformAdministration onExpiredSession={handleExpiredSession} />
          ) : null}
          {activeView === 'operations' && dashboard ? (
            <OperationsOverview
              key={dashboard.organization.organizationId}
              selectedOperationId={selectedOperationId}
              onSelectOperation={setSelectedOperationId}
              operationTask={operationTask}
              dashboard={dashboard}
              tenantAccess={tenantAccess}
              onExpiredSession={handleExpiredSession}
              onRefresh={() => loadOrganization(dashboard.organization)}
            />
          ) : null}
          {activeView === 'tenant-administration' && dashboard ? (
            <TenantAdministrationOverview
              administratorSummary={administratorSummary}
              key={dashboard.organization.organizationId}
              organization={dashboard.organization}
              tenantAccess={tenantAccess}
              onExpiredSession={handleExpiredSession}
              onStartOperation={() => navigateOperation('operation-composer')}
              onResourcesChanged={async () => {
                try {
                  const refreshed = await getJson<OrganizationAdminSummary>(
                    `/v1/organizations/${dashboard.organization.organizationId}/admin/summary`,
                  );
                  setAdministratorSummary(refreshed);
                } catch (error) {
                  if (error instanceof RequestError && error.status === 401) {
                    await handleExpiredSession();
                  }
                }
              }}
              onInvitationCreated={() =>
                setAdministratorSummary((current) =>
                  current
                    ? {
                        ...current,
                        summary: {
                          ...current.summary,
                          invitationsPending: current.summary.invitationsPending + 1,
                        },
                      }
                    : current,
                )
              }
            />
          ) : null}
        </main>

        <nav className="mobileNavigation" aria-label="Navegação móvel">
          {isPlatformWorkspace ? (
            navigationItems.map((item) => (
              <button
                aria-current={activeView === item.view ? 'page' : undefined}
                className={activeView === item.view ? 'active' : undefined}
                key={item.view}
                type="button"
                onClick={() => setActiveView(item.view)}
              >
                <ProductIcon name={item.icon} />
                <span>{item.label}</span>
              </button>
            ))
          ) : (
            <MobileOperationalNavigation
              canCreate={canCreateOperation}
              canViewAgenda={canViewAgenda}
              onNavigate={navigateOperation}
            />
          )}
        </nav>
      </div>
    </div>
  );
}

interface PageHeadingProps {
  readonly activeView: WorkspaceView;

  readonly organization: OrganizationContext | undefined;
  readonly tenantAccess: TenantAccess | undefined;
  readonly today: string;
}

function PageHeading({ activeView, organization, tenantAccess, today }: PageHeadingProps) {
  const isPlatform = activeView === 'platform';
  const isTenantAdministration = activeView === 'tenant-administration';
  const title = isPlatform
    ? 'Administração da plataforma'
    : isTenantAdministration
      ? `Administração de ${organization?.tradeName ?? 'organização'}`
      : 'Operações';
  const description = isPlatform
    ? 'Configurações e administração da plataforma.'
    : isTenantAdministration
      ? 'Gerencie os acessos e acompanhe a equipe da organização.'
      : `Acompanhe os dados reais que exigem atenção na operação de ${organization?.tradeName ?? 'sua organização'}.`;

  return (
    <section
      className={`pageHeading${activeView === 'operations' ? ' pageHeading-operations' : ''}`}
      aria-labelledby="dashboard-title"
    >
      <div>
        <span className="pageDate">{today}</span>
        <h1 id="dashboard-title">{title}</h1>
        <p>{description}</p>
      </div>
      <div className="headingContext">
        <span className="roleBadge">
          <ProductIcon name="shield" />
          {isPlatform
            ? 'Administrador da plataforma'
            : isTenantAdministration
              ? 'Administrador'
              : tenantRoleLabel(tenantAccess)}
        </span>
        <span className="moduleBadge">{isPlatform ? 'Plataforma' : 'Coordenação de Voo'}</span>
      </div>
    </section>
  );
}

function OperationsOverview({
  selectedOperationId,
  onSelectOperation,
  operationTask,
  dashboard,
  tenantAccess,
  onExpiredSession,
  onRefresh,
}: {
  readonly selectedOperationId: string | undefined;
  readonly onSelectOperation: (id: string | undefined) => void;
  readonly operationTask: { section: string; sequence: number } | undefined;
  readonly dashboard: OrganizationDashboard;
  readonly tenantAccess: TenantAccess | undefined;
  readonly onExpiredSession: () => Promise<void>;
  readonly onRefresh: () => Promise<void>;
}) {
  const canCreate = hasTenantCapability(tenantAccess, tenantCapability.operationCreate);
  const canEdit = hasTenantCapability(tenantAccess, tenantCapability.operationEdit);
  const canViewAgenda = hasTenantCapability(tenantAccess, tenantCapability.agendaView);
  const [surface, setSurface] = useState<'board' | 'agenda' | 'handoff' | 'create' | 'detail'>(
    defaultOperationalSurface(tenantAccess),
  );
  const [returnToAgenda, setReturnToAgenda] = useState(false);
  const [composerStarted, setComposerStarted] = useState(false);
  const [createSchedule, setCreateSchedule] = useState<OperationSchedulePreset>();
  const previousSurface = useRef(surface);
  const { organization, summary } = dashboard;
  useEffect(() => {
    if (!operationTask) return;
    if (operationTask.section === 'operation-composer' && canCreate) {
      setCreateSchedule(undefined);
      setReturnToAgenda(false);
      setComposerStarted(true);
      setSurface('create');
    } else if (operationTask.section === 'operation-agenda' && canViewAgenda) setSurface('agenda');
    else if (operationTask.section === 'shift-handoff') setSurface('handoff');
    else if (operationTask.section === 'operations-list') setSurface('board');
    else if (
      operationTask.section.startsWith('operation-') ||
      operationTask.section.endsWith('-decision')
    )
      setSurface('detail');
  }, [operationTask, canCreate, canViewAgenda]);
  useEffect(() => {
    if (previousSurface.current === surface) return;
    previousSurface.current = surface;
    const frame = requestAnimationFrame(() => {
      if (surface === 'board') focusOperationalSection('board-title');
      else if (surface === 'agenda') focusOperationalSection('agenda-title');
      else if (surface === 'create') focusOperationalSection('operation-composer-title');
      else if (surface === 'handoff') focusOperationalSection('shift-handoff-title');
      else focusOperationalSection('operation-detail-title');
    });
    return () => cancelAnimationFrame(frame);
  }, [surface]);
  const openOperation = (id: string) => {
    setReturnToAgenda(surface === 'agenda');
    onSelectOperation(id);
    setSurface('detail');
  };
  return (
    <>
      {surface !== 'board' ? (
        <div className="workspaceBreadcrumb">
          <button
            type="button"
            onClick={() => setSurface(surface === 'detail' && returnToAgenda ? 'agenda' : 'board')}
          >
            {surface === 'detail' && returnToAgenda ? '← Voltar à agenda' : '← Voltar às operações'}
          </button>
          <span aria-hidden="true">/</span>
          <span>
            {surface === 'agenda'
              ? 'Agenda de voos'
              : surface === 'create'
                ? 'Nova operação'
                : surface === 'handoff'
                  ? 'Passagem de turno'
                  : 'Ficha da operação'}
          </span>
        </div>
      ) : null}
      <div hidden={surface !== 'board'}>
        {canViewAgenda ? (
          <button className="agendaEntryButton" type="button" onClick={() => setSurface('agenda')}>
            ▦ Agenda de voos <span>Consultar dias, semanas e ocupação das aeronaves →</span>
          </button>
        ) : null}
        <OperationsBoard
          active={surface === 'board'}
          organizationId={organization.organizationId}
          canCreate={canCreate}
          aircraftActive={summary.aircraftActive}
          onCreate={() => {
            setCreateSchedule(undefined);
            setReturnToAgenda(false);
            setComposerStarted(true);
            setSurface('create');
          }}
          onOpenOperation={openOperation}
          onHandoff={() => setSurface('handoff')}
          onExpiredSession={onExpiredSession}
        />
      </div>
      {canViewAgenda ? (
        <div hidden={surface !== 'agenda'}>
          <OperationAgenda
            active={surface === 'agenda'}
            organizationId={organization.organizationId}
            onOpenOperation={openOperation}
            onCreate={
              canCreate
                ? (preset) => {
                    setCreateSchedule(preset);
                    setReturnToAgenda(true);
                    setComposerStarted(true);
                    setSurface('create');
                  }
                : undefined
            }
            onExpiredSession={onExpiredSession}
          />
        </div>
      ) : null}
      {surface === 'handoff' ? (
        <ShiftHandoffPanel
          canEdit={canEdit}
          organizationId={organization.organizationId}
          onExpiredSession={onExpiredSession}
          onOpenOperation={openOperation}
        />
      ) : null}
      {composerStarted && canCreate ? (
        <div hidden={surface !== 'create'}>
          <OperationCreator
            key={createSchedule?.plannedDeparture ?? 'default-operation-schedule'}
            organizationId={organization.organizationId}
            {...(createSchedule ? { initialSchedule: createSchedule } : {})}
            onCancel={() => {
              setComposerStarted(false);
              setCreateSchedule(undefined);
              setSurface(returnToAgenda ? 'agenda' : 'board');
            }}
            onCreated={(id) => {
              setComposerStarted(false);
              setCreateSchedule(undefined);
              onSelectOperation(id);
              setSurface('detail');
            }}
            onImported={onRefresh}
            onExpiredSession={onExpiredSession}
          />
        </div>
      ) : null}
      {selectedOperationId ? (
        <div hidden={surface !== 'detail'}>
          <OperationDetailView
            key={`${organization.organizationId}:${selectedOperationId}`}
            active={surface === 'detail'}
            canEdit={canEdit}
            operationId={selectedOperationId}
            organizationId={organization.organizationId}
            requestedSection={operationTask}
            onClose={() => setSurface(returnToAgenda ? 'agenda' : 'board')}
            onExpiredSession={onExpiredSession}
          />
        </div>
      ) : null}
      {!canEdit ? (
        <p className="readOnlyNote">
          Modo de consulta · Para alterar uma operação, procure o coordenador da sua organização.
        </p>
      ) : null}
    </>
  );
}
interface TenantAdministrationOverviewProps {
  readonly administratorSummary: OrganizationAdminSummary | undefined;
  readonly organization: OrganizationContext;
  readonly tenantAccess: TenantAccess | undefined;
  readonly onExpiredSession: () => Promise<void>;
  readonly onInvitationCreated: () => void;
  readonly onResourcesChanged: () => Promise<void>;
  readonly onStartOperation: () => void;
}

function TenantAdministrationOverview({
  administratorSummary,
  organization,
  tenantAccess,
  onExpiredSession,
  onInvitationCreated,
  onResourcesChanged,
  onStartOperation,
}: TenantAdministrationOverviewProps) {
  const canCreateInvitation = hasTenantCapability(tenantAccess, tenantCapability.invitationCreate);
  const [invitationEmail, setInvitationEmail] = useState('');
  const [invitationRole, setInvitationRole] = useState<InvitableTenantRole>('viewer');
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const [invitationFeedback, setInvitationFeedback] = useState<{
    readonly tone: 'success' | 'error';
    readonly text: string;
  }>();
  const [isRunningIsolationProbe, setIsRunningIsolationProbe] = useState(false);
  const [isolationProbeResult, setIsolationProbeResult] = useState<TenantIsolationProbeResult>();
  const [isolationProbeError, setIsolationProbeError] = useState<string>();

  const handleIsolationProbe = async () => {
    setIsRunningIsolationProbe(true);
    setIsolationProbeResult(undefined);
    setIsolationProbeError(undefined);

    try {
      const response = await fetch(
        `/v1/organizations/${organization.organizationId}/homologation/tenant-isolation`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (response.status === 403) {
        setIsolationProbeError(
          'Sua conta não possui mais permissão administrativa nesta organização.',
        );
        return;
      }
      if (response.status === 503) {
        setIsolationProbeError('Os alvos da homologação não estão configurados neste ambiente.');
        return;
      }

      const result = (await response.json()) as TenantIsolationProbeResult;
      if (response.ok || response.status === 502) {
        setIsolationProbeResult(result);
        return;
      }
      setIsolationProbeError('Não foi possível executar a verificação de isolamento.');
    } catch {
      setIsolationProbeError('Não foi possível executar a verificação. Verifique sua conexão.');
    } finally {
      setIsRunningIsolationProbe(false);
    }
  };

  const handleInvitationSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSendingInvitation(true);
    setInvitationFeedback(undefined);

    try {
      const response = await fetch(`/v1/organizations/${organization.organizationId}/invitations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: invitationEmail, roleKey: invitationRole }),
      });

      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) {
        const text =
          response.status === 403
            ? 'Sua conta não possui mais permissão para convidar pessoas nesta organização.'
            : response.status === 503
              ? 'O serviço de e-mail está indisponível. Nenhum convite foi enviado.'
              : response.status === 400
                ? 'Informe um endereço de e-mail válido.'
                : 'Não foi possível enviar o convite. Tente novamente.';
        setInvitationFeedback({ tone: 'error', text });
        return;
      }

      const result = (await response.json()) as InvitationCreatedResponse;
      const expiresAt = new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(result.invitation.expiresAt));
      setInvitationFeedback({
        tone: 'success',
        text: `Convite para ${result.invitation.role.name} enviado a ${result.invitation.email}. Válido até ${expiresAt}.`,
      });
      setInvitationEmail('');
      setInvitationRole('viewer');
      onInvitationCreated();
    } catch {
      setInvitationFeedback({
        tone: 'error',
        text: 'Não foi possível enviar o convite. Verifique sua conexão e tente novamente.',
      });
    } finally {
      setIsSendingInvitation(false);
    }
  };

  if (!administratorSummary) {
    return (
      <section className="platformFoundation" aria-labelledby="tenant-admin-unavailable-title">
        <div className="platformFoundationIcon">
          <ProductIcon name="shield" />
        </div>
        <div>
          <span className="sectionEyebrow">Administração da organização</span>
          <h2 id="tenant-admin-unavailable-title">Resumo administrativo indisponível</h2>
          <p>
            O painel operacional continua separado e disponível. Nenhuma ação administrativa foi
            exibida sem a confirmação da API autorizadora.
          </p>
        </div>
      </section>
    );
  }

  const items = [
    {
      label: 'Pessoas ativas',
      value: administratorSummary.summary.membersActive,
      detail: 'com vínculo nesta organização',
      icon: 'people' as const,
      tone: 'teal',
    },
    {
      label: 'Convites pendentes',
      value: administratorSummary.summary.invitationsPending,
      detail: 'aguardando aceite',
      icon: 'pending' as const,
      tone: administratorSummary.summary.invitationsPending > 0 ? 'amber' : 'neutral',
    },
    {
      label: 'Aeronaves ativas',
      value: administratorSummary.summary.aircraftActive,
      detail: 'no contexto da organização',
      icon: 'aircraft' as const,
      tone: 'green',
    },
  ];

  return (
    <>
      <section className="adminFoundation" aria-labelledby="tenant-admin-title">
        <div>
          <span className="sectionEyebrow">Administração da organização</span>
          <h2 id="tenant-admin-title">{organization.tradeName}</h2>
          <p>Acompanhe a equipe e convide as pessoas que vão trabalhar com você.</p>
        </div>
        <span>Equipe e acessos</span>
      </section>
      <section className="summaryGrid adminSummaryGrid" aria-label="Resumo administrativo">
        {items.map((item) => (
          <article className="summaryCard" key={item.label}>
            <span className={`summaryIcon summaryIcon-${item.tone}`}>
              <ProductIcon name={item.icon} />
            </span>
            <div>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <small>{item.detail}</small>
            </div>
          </article>
        ))}
      </section>
      <OrganizationProfile
        organizationId={organization.organizationId}
        onChanged={onResourcesChanged}
        onExpiredSession={onExpiredSession}
      />
      <OrganizationSetup
        invitationsPending={administratorSummary.summary.invitationsPending}
        membersActive={administratorSummary.summary.membersActive}
        operationsTotal={administratorSummary.summary.operationsTotal}
        organizationId={organization.organizationId}
        onChanged={onResourcesChanged}
        onExpiredSession={onExpiredSession}
        onStartOperation={onStartOperation}
      />
      <OrganizationTeam
        organizationId={organization.organizationId}
        refreshSignal={
          administratorSummary.summary.membersActive +
          administratorSummary.summary.invitationsPending
        }
        onChanged={onResourcesChanged}
        onExpiredSession={onExpiredSession}
      />
      <PilotReadiness
        organizationId={organization.organizationId}
        onExpiredSession={onExpiredSession}
      />
      {administratorSummary.homologation?.tenantIsolationAvailable ? (
        <details className="isolationProbeDetails">
          <summary>Verificação técnica de acesso</summary>
          <section className="isolationProbePanel" aria-labelledby="isolation-probe-title">
            <div>
              <span className="sectionEyebrow">Homologação de segurança</span>
              <h2 id="isolation-probe-title">Verificar isolamento entre organizações</h2>
              <p>
                Executa duas leituras HTTP reais com esta sessão: uma no contexto autorizado e outra
                em uma organização estrangeira. Nenhum usuário, senha ou sessão adicional é criado.
              </p>
            </div>
            <div className="isolationProbeAction">
              <button
                className="primaryButton"
                disabled={isRunningIsolationProbe}
                type="button"
                onClick={() => void handleIsolationProbe()}
              >
                {isRunningIsolationProbe ? 'Verificando…' : 'Executar verificação'}
              </button>
              {isolationProbeResult ? (
                <div
                  className={`isolationProbeResult ${
                    isolationProbeResult.passed
                      ? 'isolationProbeResult-success'
                      : 'isolationProbeResult-error'
                  }`}
                  role="status"
                >
                  <strong>{isolationProbeResult.passed ? 'APROVADO' : 'REPROVADO'}</strong>
                  <span>Tenant autorizado: HTTP {isolationProbeResult.ownTenant.status}</span>
                  <span>
                    Tenant estrangeiro: HTTP {isolationProbeResult.foreignTenant.status}
                    {isolationProbeResult.foreignTenant.code
                      ? ` · ${isolationProbeResult.foreignTenant.code}`
                      : ''}
                  </span>
                </div>
              ) : null}
              {isolationProbeError ? (
                <p className="isolationProbeMessage" role="alert">
                  {isolationProbeError}
                </p>
              ) : null}
            </div>
          </section>
        </details>
      ) : null}
      {canCreateInvitation ? (
        <section className="invitationPanel" aria-labelledby="invitation-title">
          <div>
            <span className="sectionEyebrow">Acesso por convite</span>
            <h2 id="invitation-title">Convidar uma pessoa para {organization.tradeName}</h2>
            <p>
              O e-mail receberá um link individual para criar a conta e ingressar somente nesta
              organização. O link expira em 72 horas.
            </p>
          </div>
          <form className="invitationForm" onSubmit={(event) => void handleInvitationSubmit(event)}>
            <div className="invitationField">
              <label htmlFor="invitation-email">E-mail profissional</label>
              <input
                autoComplete="email"
                id="invitation-email"
                onChange={(event) => setInvitationEmail(event.target.value)}
                placeholder="nome@empresa.com.br"
                required
                type="email"
                value={invitationEmail}
              />
            </div>
            <div className="invitationField">
              <label htmlFor="invitation-role">Função inicial</label>
              <select
                id="invitation-role"
                onChange={(event) => setInvitationRole(event.target.value as InvitableTenantRole)}
                value={invitationRole}
              >
                {invitationRoleOptions.map((role) => (
                  <option key={role.key} value={role.key}>
                    {role.name} — {role.detail}
                  </option>
                ))}
              </select>
              <small>O acesso fica limitado a esta organização e pode ser revogado.</small>
            </div>
            <button className="primaryButton" disabled={isSendingInvitation} type="submit">
              {isSendingInvitation ? 'Enviando…' : 'Enviar convite'}
            </button>
            {invitationFeedback ? (
              <p
                className={`invitationFeedback invitationFeedback-${invitationFeedback.tone}`}
                role={invitationFeedback.tone === 'error' ? 'alert' : 'status'}
              >
                {invitationFeedback.text}
              </p>
            ) : null}
          </form>
        </section>
      ) : null}
      <section className="tenantNotice" aria-label="Limite administrativo">
        <ProductIcon name="shield" />
        <div>
          <strong>Administração limitada a {organization.tradeName}</strong>
          <span>Este papel não concede acesso global nem acesso a outra organização.</span>
        </div>
        <span className="tenantStatus">Somente esta organização</span>
      </section>
    </>
  );
}
